import React, { useState, useRef, useEffect } from 'react';
import { DayPicker } from 'react-day-picker';
import { format as dateFnsFormat, parse } from 'date-fns';
import { getAppConfig } from 'Core/config/appConfig';
import { StyledFormElement } from 'Core/components/FormElements/formElements.styled';
import 'react-day-picker/src/style.css';
import './CalendarInput.css';
import Error from 'Core/components/FormElements/Error';

const CalendarInputField = (props) => {
  const { dateFormat } = getAppConfig();
  const {
    errors = null,
    className = 'form-control',
    name,
    id = name,
    testid = id,
    label,
    format = dateFormat,
    onChange,
    onBlur,
    value: controlledValue,
    dayPickerProps,
    // When true, every keystroke (including clearing the input) is reported to the
    // form, so schema validation sees exactly what the user typed. When false,
    // invalid/empty input keeps the last valid value in form state.
    emitRawValue = false,
    // Marks the label with an asterisk; enforcement stays in the form schema
    required = false,
  } = props;

  const [selected, setSelected] = useState();
  const [inputValue, setInputValue] = useState(controlledValue || '');
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const inputRef = useRef(null);
  const containerRef = useRef(null);

  const error = errors[name]?.message;

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsPickerOpen(false);
      }
    };
    if (isPickerOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isPickerOpen]);

  const handleDaySelect = (date) => {
    setSelected(date);
    if (date) {
      const formatted = dateFnsFormat(date, format);
      setInputValue(formatted);
      onChange(formatted);
    }
    setIsPickerOpen(false);
  };

  const handleInputChange = (e) => {
    setInputValue(e.target.value);
    const parsed = parse(e.target.value, format, new Date());
    if (!isNaN(parsed)) {
      setSelected(parsed);
    }
    if (emitRawValue || !isNaN(parsed)) {
      onChange(e.target.value);
    }
  };

  return (
    <StyledFormElement className={className}>
      <label htmlFor={id}>
        {label}
        {required && <span className="text-red-500"> *</span>}
      </label>
      <div ref={containerRef} className={className} style={{ position: 'relative' }}>
        <input
          ref={inputRef}
          type="text"
          id={id}
          data-testid={testid}
          value={inputValue}
          onChange={handleInputChange}
          onBlur={onBlur}
          aria-required={required}
          onFocus={() => setIsPickerOpen(true)}
          placeholder={format}
        />
        {isPickerOpen && (
          <div
            style={{
              position: 'absolute',
              zIndex: 1000,
              background: 'white',
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            }}
          >
            <DayPicker
              mode="single"
              selected={selected}
              onSelect={handleDaySelect}
              data-testid="day-picker"
              data-date-picker-props={dayPickerProps}
            />
          </div>
        )}
      </div>
      <Error message={error} show={Boolean(error)} />
    </StyledFormElement>
  );
};

export default CalendarInputField;
